import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { Check, CircleAlert, Copy, MessageCircle, RefreshCw, TriangleAlert, X } from 'lucide-react'
import { Avatar, Button, IconButton, Notice, Skeleton, SkeletonGroup, TextField, copyText } from '@/components/play'
import { personToneKey } from '@/lib/profile'
import { inviteError, invitesApi, onInvitedChange, type GuestLink, type InviteError, type InvitedGuest } from '@/lib/watch/invitesApi'
import { STAGE_SEATS, initialsSource, nameOrHandle, profileAvatarUrl, sentenceName } from '@/lib/watch/rules'
import { cn } from '@/lib/utils'
import './invites.css'

/** The panel's title, for the Popover or sheet that holds it (the panel itself has no heading). */
export const INVITE_TO_STAGE_TITLE = 'Invite to your stage'

export interface InviteToStageProps {
  stageId: string
  /** Seats taken now, hosts included (the stage's live members). */
  seatsTaken: number
  /** Seats on a stage (`Stage::SEATS`). */
  seatsTotal?: number
  className?: string
}

type LinkState = { status: 'loading' } | { status: 'ready'; link: GuestLink } | { status: 'failed'; error: InviteError }

/** What the status line under the actions says: the last copy or New link, and how it went. */
type Note = 'copied' | 'copy_failed' | 'rotated' | { rotateFailed: string } | null

/** How long Copied stays before the line clears. */
const COPIED_MS = 2000
/** How often "Expires in …" is worked out again. */
const TICK_MS = 60_000

/**
 * "Invite to your stage" (docs/features/prototypes/watch-experience, `openInvite`): the seats as dots, the stage's
 * guest link (fetched with `getStageLink`, made if there is none) read-only with how long it lasts and how many more can
 * use it, **Copy link** and **New link** (`rotateStageLink`, the old link stops working), and the hint that chat works
 * too. A full stage says so instead of offering the link. Copied shows only once the clipboard has the link; when it
 * refuses, the link is selected so it can be copied by hand. Under it, **Waiting** (`WaitingList`): the people invited
 * who haven't joined, each with Cancel. They hold no seat (`Stage::SEATS` counts live members, as `stage_full` does),
 * so the dots leave them out. Put it in a Popover (desktop) or WatchSheet (phone) titled `INVITE_TO_STAGE_TITLE`.
 */
export function InviteToStage({ stageId, seatsTaken, seatsTotal = STAGE_SEATS, className }: InviteToStageProps) {
  const total = Math.max(0, seatsTotal)
  const taken = Math.min(Math.max(0, seatsTaken), total)
  const full = taken >= total

  return (
    <div className={cn('watch-invite', className)}>
      <p className="watch-invite__seats">
        <span className="watch-invite__dots" aria-hidden="true">
          {Array.from({ length: total }, (_, seat) => (
            <span key={seat} className="watch-invite__dot" data-taken={seat < taken || undefined} />
          ))}
        </span>
        {taken} of {total} seats taken
      </p>
      {full ? (
        <Notice title="Your stage is full">Remove someone from the stage to invite another person.</Notice>
      ) : (
        <>
          <StageLink stageId={stageId} />
          <p className="watch-invite__hint">
            <MessageCircle aria-hidden="true" />
            Or invite someone from chat
          </p>
        </>
      )}
      <WaitingList key={stageId} stageId={stageId} seatsTaken={taken} full={full} />
    </div>
  )
}

type WaitingState = { status: 'loading' } | { status: 'ready'; people: InvitedGuest[] } | { status: 'failed'; error: InviteError }

/** What the Waiting list's status line says: the last cancel, and how it went. */
type WaitingNote = { cancelled: string } | { gone: string } | { failed: string } | null

/**
 * Who is invited on the stage and hasn't joined (`invitesApi.invited`), oldest first, as the API lists them: avatar,
 * name, @handle and how long ago they started waiting, each with **Cancel** (`cancelInvitation`, quiet: no card). Read
 * when the panel opens, after an Invite on stage on this stage goes through (`onInvitedChange`), and whenever a seat is
 * taken or freed (someone waiting may have joined). Nothing shows while nobody waits. A cancel's row goes on the
 * server's 204; a 404 (they joined, declined, or it lapsed) says so and reads the list again, the row staying until
 * that answer. Keyed by stage, so a stage change starts afresh.
 */
function WaitingList({ stageId, seatsTaken, full }: { stageId: string; seatsTaken: number; full: boolean }) {
  const [state, setState] = useState<WaitingState>({ status: 'loading' })
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set())
  const [note, setNote] = useState<WaitingNote>(null)
  const headingId = useId()
  const now = useNow(TICK_MS)
  /** The latest read; an answer from an earlier one is dropped. */
  const readRef = useRef(0)
  /** The read still waiting for its answer, if any. */
  const pendingRef = useRef<number | null>(null)
  const statusRef = useRef<HTMLParagraphElement>(null)
  const buttons = useRef(new Map<string, HTMLButtonElement>())
  /** The person whose Cancel had focus last, so focus has somewhere to go when their row goes. */
  const focusedRef = useRef<string | null>(null)
  const shownRef = useRef<InvitedGuest[]>([])

  const load = useCallback(async () => {
    const read = ++readRef.current
    pendingRef.current = read
    try {
      const people = await invitesApi.invited(stageId)
      if (read === readRef.current) setState({ status: 'ready', people })
    } catch (error) {
      if (read === readRef.current) setState({ status: 'failed', error: inviteError(error) })
    } finally {
      if (pendingRef.current === read) pendingRef.current = null
    }
  }, [stageId])

  useEffect(() => {
    void load()
  }, [load, seatsTaken])

  useEffect(() => onInvitedChange((changed) => changed === stageId && void load()), [load, stageId])

  useEffect(
    () => () => {
      // Unmounted: whatever is on its way answers nobody.
      readRef.current++
    },
    [],
  )

  const people = state.status === 'ready' ? state.people : null

  // A row that goes while its Cancel had focus hands focus to the row now in its place, else the one before, else to
  // what the status line says.
  useLayoutEffect(() => {
    const before = shownRef.current
    shownRef.current = people ?? []
    const gone = focusedRef.current
    if (!gone || people?.some((person) => person.user.id === gone)) return
    focusedRef.current = null
    const active = document.activeElement
    if (active && active !== document.body) return
    const index = before.findIndex((person) => person.user.id === gone)
    const next = people?.[Math.min(Math.max(index, 0), people.length - 1)]
    const target = next ? buttons.current.get(next.user.id) : statusRef.current
    target?.focus()
  }, [people])

  const setPersonBusy = (userId: string, on: boolean) =>
    setBusy((current) => {
      const next = new Set(current)
      if (on) next.add(userId)
      else next.delete(userId)
      return next
    })

  const cancel = async (person: InvitedGuest) => {
    const { id } = person.user
    if (busy.has(id)) return
    const who = sentenceName(person.user)
    setPersonBusy(id, true)
    setNote(null)
    try {
      await invitesApi.cancelInvitation(stageId, id)
      // A read that started before the cancel landed may still list them: drop it and read again.
      const overtaken = pendingRef.current !== null
      readRef.current++
      setState((current) => (current.status === 'ready' ? { ...current, people: current.people.filter((other) => other.user.id !== id) } : current))
      setNote({ cancelled: who })
      setPersonBusy(id, false)
      if (overtaken) void load()
    } catch (failure) {
      const error = inviteError(failure)
      if (error.type === 'not_found') {
        setNote({ gone: who })
        await load()
      } else {
        setNote({ failed: cancelFailure(error, who) })
      }
      setPersonBusy(id, false)
    }
  }

  const heading = full ? 'Waiting for a seat' : 'Waiting'
  return (
    <section className="watch-invite__waiting" aria-labelledby={people?.length ? headingId : undefined}>
      {people && people.length > 0 && (
        <>
          <h3 id={headingId} className="watch-invite__heading">
            {heading}
          </h3>
          {/* role="list": Safari drops list semantics from lists styled without markers. */}
          <ul className="watch-invite__people" role="list" aria-labelledby={headingId}>
            {people.map((person) => {
              const { user } = person
              const named = nameOrHandle(user.name, null, '')
              const when = waitedSince(person.invited_at, now)
              return (
                <li key={user.id} className="watch-invite__person">
                  <Avatar
                    name={initialsSource(user.name, user.handle)}
                    src={profileAvatarUrl(user.avatar_url)}
                    size="sm"
                    tone="auto"
                    toneKey={personToneKey(user)}
                  />
                  <span className="watch-invite__who">
                    <bdi className="watch-invite__name">{named || (user.handle ? `@${user.handle}` : 'Someone')}</bdi>
                    <span className="watch-invite__when">
                      {named && user.handle && <span className="watch-invite__handle">@{user.handle}</span>}
                      {named && user.handle && when && ' · '}
                      {when && <time dateTime={person.invited_at}>{when}</time>}
                    </span>
                  </span>
                  <IconButton
                    ref={(node) => {
                      if (node) buttons.current.set(user.id, node)
                      else buttons.current.delete(user.id)
                    }}
                    variant="ghost"
                    size="compact"
                    label={`Cancel ${sentenceName(user)}’s invitation`}
                    loading={busy.has(user.id)}
                    onFocus={() => {
                      focusedRef.current = user.id
                    }}
                    onBlur={(event) => {
                      // Only when focus went somewhere: a browser may blur a button as its row is taken out.
                      if (event.relatedTarget && focusedRef.current === user.id) focusedRef.current = null
                    }}
                    onClick={() => void cancel(person)}
                  >
                    <X />
                  </IconButton>
                </li>
              )
            })}
          </ul>
        </>
      )}
      {state.status === 'failed' && <WaitingFailure error={state.error} onRetry={() => void load()} />}
      {/* Always there, so a message that appears in it is announced; focus lands on it when the last row goes. */}
      <p ref={statusRef} role="status" tabIndex={-1} className="watch-invite__status" data-tone={note && 'failed' in note ? 'error' : undefined}>
        <WaitingNoteWords note={note} />
      </p>
    </section>
  )
}

function WaitingNoteWords({ note }: { note: WaitingNote }) {
  if (!note) return null
  if ('cancelled' in note) return <>Cancelled {note.cancelled}’s invitation</>
  if ('gone' in note) return <>{note.gone} already joined or the invitation lapsed.</>
  return (
    <>
      <CircleAlert aria-hidden="true" />
      {note.failed}
    </>
  )
}

function WaitingFailure({ error, onRetry }: { error: InviteError; onRetry: () => void }) {
  const { words, retry } = waitingFailure(error)
  return (
    <p className="watch-invite__status watch-invite__waiting-failed" data-tone="error">
      <CircleAlert aria-hidden="true" />
      <span>{words}</span>
      {retry && (
        <Button variant="ghost" size="compact" onClick={onRetry}>
          Check again
        </Button>
      )}
    </p>
  )
}

/** What a failed read of the Waiting list says, and whether reading again can help. */
function waitingFailure(error: InviteError): { words: string; retry: boolean } {
  switch (error.type) {
    case 'forbidden':
      return { words: 'Only the stage’s hosts can see who’s waiting.', retry: false }
    case 'not_found':
      return { words: 'This stage is no longer here.', retry: false }
    case 'unauthorized':
      return { words: 'Sign in again to see who’s waiting.', retry: false }
    case 'network':
    case 'rate_limited':
      return { words: error.message, retry: true }
    default:
      return { words: 'We couldn’t check who’s waiting.', retry: true }
  }
}

function cancelFailure(error: InviteError, who: string): string {
  if (error.type === 'network' || error.type === 'rate_limited' || error.type === 'forbidden') return error.message
  return `We couldn’t cancel ${who}’s invitation. Try again.`
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS

/**
 * How long ago someone started waiting (`invited_at`): "just now" under a minute (or a clock a little ahead), else whole
 * minutes, then whole hours, rounded down; '' when the time doesn't read. An invitation lapses after a day.
 */
export function waitedSince(invitedAt: string, now = Date.now()): string {
  const at = Date.parse(invitedAt)
  if (!Number.isFinite(at)) return ''
  const age = now - at
  if (age < MINUTE_MS) return 'just now'
  if (age < HOUR_MS) return `${unitWords(Math.floor(age / MINUTE_MS), 'minute')} ago`
  return `${unitWords(Math.floor(age / HOUR_MS), 'hour')} ago`
}

function StageLink({ stageId }: { stageId: string }) {
  const [state, setState] = useState<LinkState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [rotating, setRotating] = useState(false)
  const [note, setNote] = useState<Note>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  /** The stage the panel shows now, so an answer for another one is dropped. */
  const stageRef = useRef(stageId)
  stageRef.current = stageId
  const now = useNow(TICK_MS)

  useEffect(() => {
    let current = true
    setState({ status: 'loading' })
    setNote(null)
    invitesApi.getStageLink(stageId).then(
      (link) => current && setState({ status: 'ready', link }),
      (error: unknown) => current && setState({ status: 'failed', error: inviteError(error) }),
    )
    return () => {
      current = false
    }
  }, [stageId, attempt])

  useEffect(() => {
    if (note !== 'copied') return
    const timer = window.setTimeout(() => setNote(null), COPIED_MS)
    return () => window.clearTimeout(timer)
  }, [note])

  if (state.status === 'loading') {
    return (
      <SkeletonGroup label="Getting your stage link" className="watch-invite__link">
        <Skeleton shape="field" />
        <Skeleton shape="button" className="watch-invite__actions" />
      </SkeletonGroup>
    )
  }

  if (state.status === 'failed') {
    const { words, retry } = loadFailure(state.error)
    return (
      <Notice
        tone="error"
        action={
          retry ? (
            <Button variant="secondary" size="compact" onClick={() => setAttempt((count) => count + 1)}>
              Try again
            </Button>
          ) : undefined
        }
      >
        {words}
      </Notice>
    )
  }

  const { link } = state
  const lapsed = linkLapsed(link, now)
  const metaId = `watch-invite-meta-${link.id}`

  // Started in the click itself, so the browser still counts the press as permission to write the clipboard.
  const copy = () => {
    const url = link.url
    void copyText(url).then((copied) => {
      if (stageRef.current !== stageId) return
      setNote(copied ? 'copied' : 'copy_failed')
      if (!copied) {
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    })
  }

  const rotate = async () => {
    if (rotating) return
    setRotating(true)
    setNote(null)
    try {
      const fresh = await invitesApi.rotateStageLink(stageId)
      if (stageRef.current !== stageId) return
      setState({ status: 'ready', link: fresh })
      setNote('rotated')
    } catch (error) {
      if (stageRef.current !== stageId) return
      setNote({ rotateFailed: rotateFailure(inviteError(error)) })
    } finally {
      setRotating(false)
    }
  }

  return (
    <div className="watch-invite__link">
      <TextField
        ref={inputRef}
        label="Stage link"
        value={link.url}
        readOnly
        mono
        onFocus={(event) => event.currentTarget.select()}
        description={
          <span id={metaId} className="watch-invite__meta" data-lapsed={lapsed || undefined}>
            {lapsed && <TriangleAlert aria-hidden="true" />}
            {linkMeta(link, now)}
          </span>
        }
      />
      <div className="watch-invite__actions">
        <Button
          variant="secondary"
          className="watch-invite__copy"
          disabled={lapsed}
          focusableWhenDisabled
          aria-describedby={lapsed ? metaId : undefined}
          onClick={copy}
        >
          {note === 'copied' ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          Copy link
        </Button>
        <Button variant={lapsed ? 'primary' : 'ghost'} loading={rotating} onClick={() => void rotate()}>
          <RefreshCw aria-hidden="true" />
          New link
        </Button>
      </div>
      {/* Always there, so a message that appears in it is announced. */}
      <p role="status" className="watch-invite__status" data-tone={note === 'copy_failed' || isRotateFailure(note) ? 'error' : undefined}>
        <NoteWords note={note} />
      </p>
    </div>
  )
}

function NoteWords({ note }: { note: Note }) {
  if (note === 'copied') return <>Copied</>
  if (note === 'rotated') return <>New link ready. The old one no longer works.</>
  if (note === 'copy_failed') {
    return (
      <>
        <CircleAlert aria-hidden="true" />
        Couldn’t copy. The link is selected, so copy it from there.
      </>
    )
  }
  if (isRotateFailure(note)) {
    return (
      <>
        <CircleAlert aria-hidden="true" />
        {note.rotateFailed}
      </>
    )
  }
  return null
}

const isRotateFailure = (note: Note): note is { rotateFailed: string } => typeof note === 'object' && note !== null

/** What a failed load says, and whether trying again can help. */
function loadFailure(error: InviteError): { words: string; retry: boolean } {
  switch (error.type) {
    case 'forbidden':
      return { words: 'Only the stage’s hosts can invite people on stage.', retry: false }
    case 'not_found':
      return { words: 'This stage is no longer here.', retry: false }
    case 'unauthorized':
      return { words: 'Sign in again to invite people on stage.', retry: false }
    case 'network':
    case 'rate_limited':
      return { words: error.message, retry: true }
    default:
      return { words: 'We couldn’t get your stage link. Try again.', retry: true }
  }
}

function rotateFailure(error: InviteError): string {
  if (error.type === 'network' || error.type === 'rate_limited' || error.type === 'forbidden') return error.message
  return 'We couldn’t make a new link. Try again.'
}

/** The link can't bring anyone else on: it has expired or has no uses left. */
export function linkLapsed(link: GuestLink, now = Date.now()): boolean {
  const expires = Date.parse(link.expires_at)
  return link.uses_left <= 0 || (Number.isFinite(expires) && expires <= now)
}

const unitWords = (value: number, unit: 'minute' | 'hour') => new Intl.NumberFormat('en', { style: 'unit', unit, unitDisplay: 'long' }).format(value)

/** "Expires in 24 hours · 10 uses left", or what has run out. */
export function linkMeta(link: GuestLink, now = Date.now()): string {
  const left = Date.parse(link.expires_at) - now
  const uses = link.uses_left <= 0 ? 'No uses left' : link.uses_left === 1 ? '1 use left' : `${link.uses_left.toLocaleString('en')} uses left`
  if (!Number.isFinite(left)) return uses
  if (left <= 0) return link.uses_left <= 0 ? `Expired · ${uses}` : 'Expired'
  const minutes = Math.ceil(left / 60_000)
  const expires = minutes < 60 ? unitWords(minutes, 'minute') : unitWords(Math.round(minutes / 60), 'hour')
  return `Expires in ${expires} · ${uses}`
}

/** The time now, again every `everyMs`. */
function useNow(everyMs: number) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), everyMs)
    return () => window.clearInterval(timer)
  }, [everyMs])
  return now
}
